import { useTheme } from "next-themes";
import { Moon, Sun, Monitor, Type, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTextSize, TEXT_SIZES } from "@/lib/appearance";

export const THEMES = [["dark", "Dark", Moon], ["light", "Light", Sun], ["system", "System", Monitor]];

const Item = ({ on, onSelect, testId, children }) => (
  <DropdownMenuItem data-testid={testId} onSelect={(e) => { e.preventDefault(); onSelect(); }} className="cursor-pointer gap-2">
    {children}<Check className={`ml-auto h-4 w-4 text-aqua ${on ? "opacity-100" : "opacity-0"}`} />
  </DropdownMenuItem>
);

export const AppearanceMenu = ({ className = "" }) => {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [size, setSize] = useTextSize();
  const Icon = resolvedTheme === "light" ? Sun : Moon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="appearance-menu-btn" aria-label="Appearance settings"
        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line bg-surface/60 text-ink-2 transition-colors hover:border-aqua/60 hover:text-ink ${className}`}>
        <Icon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-ink-3">Appearance</DropdownMenuLabel>
        {THEMES.map(([k, l, I]) => (
          <Item key={k} on={theme === k} onSelect={() => setTheme(k)} testId={`appearance-theme-${k}`}><I className="h-4 w-4" />{l}</Item>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-ink-3"><Type className="h-3.5 w-3.5" />Text size</DropdownMenuLabel>
        {TEXT_SIZES.map(([k, l]) => (
          <Item key={k} on={size === k} onSelect={() => setSize(k)} testId={`appearance-size-${k}`}>{l}</Item>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Pill = ({ on, onClick, testId, children }) => (
  <button type="button" data-testid={testId} onClick={onClick} aria-pressed={on}
    className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${on ? "bg-solid text-white" : "text-ink-2 hover:bg-elevated hover:text-ink"}`}>{children}</button>
);

export const AppearanceSettings = () => {
  const { theme, setTheme } = useTheme();
  const [size, setSize] = useTextSize();
  return (
    <div data-testid="appearance-settings" className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-sm font-semibold text-ink">Appearance</p>
        <div className="flex gap-1 rounded-xl border border-line bg-canvas-2 p-1">
          {THEMES.map(([k, l, I]) => <Pill key={k} on={theme === k} onClick={() => setTheme(k)} testId={`profile-theme-${k}`}><I className="h-4 w-4" />{l}</Pill>)}
        </div>
        <p className="mt-1.5 text-xs text-ink-3">System follows your device setting. Saved on this device.</p>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-ink">Text size</p>
        <div className="flex gap-1 rounded-xl border border-line bg-canvas-2 p-1">
          {TEXT_SIZES.map(([k, l]) => <Pill key={k} on={size === k} onClick={() => setSize(k)} testId={`profile-size-${k}`}>{l}</Pill>)}
        </div>
      </div>
    </div>
  );
};
